import React from 'react';
import { Link } from 'react-router-dom';
import { Cog, Minimize2, Sparkles, Workflow, Compass, Boxes, Map, Code2, Rocket, CheckCircle2, Blocks, Handshake, ArrowRight, X, ZoomIn, Briefcase, Users } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import PageHeader from '../components/PageHeader';
import HomeFeatures from '../components/HomeFeatures';

const Methodology: React.FC = () => {
  const { t } = useLanguage();
  const id = t.methodology.idesign;
  const [activeImage, setActiveImage] = React.useState<{ src: string; alt: string } | null>(null);

  const painCategories = [
    { cat: 'project', icon: Briefcase, label: t.methodology.pains.categoryProject },
    { cat: 'project', icon: Briefcase, label: t.methodology.pains.categoryProject },
    { cat: 'project', icon: Briefcase, label: t.methodology.pains.categoryProject },
    { cat: 'tech', icon: Code2, label: t.methodology.pains.categoryTech },
    { cat: 'project', icon: Briefcase, label: t.methodology.pains.categoryProject },
    { cat: 'team', icon: Users, label: t.methodology.pains.categoryTeam },
    { cat: 'team', icon: Users, label: t.methodology.pains.categoryTeam },
    { cat: 'project', icon: Briefcase, label: t.methodology.pains.categoryProject },
    { cat: 'tech', icon: Code2, label: t.methodology.pains.categoryTech },
  ];

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActiveImage(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const squareThumb = (src: string, alt: string) => (
    <button
      type="button"
      title={alt}
      aria-label={alt}
      className="relative w-full aspect-square overflow-hidden rounded-xl border border-maat-sand/60 bg-white cursor-zoom-in group/img"
      onClick={() => setActiveImage({ src, alt })}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover transition-transform duration-500 group-hover/img:scale-105"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-maat-blue/45 opacity-0 group-hover/img:opacity-100 transition-opacity duration-300 flex items-center justify-center">
        <span className="bg-white/95 text-maat-blue px-3 py-1.5 rounded-full text-[11px] font-semibold flex items-center gap-1.5 shadow-lg">
          <ZoomIn size={13} className="text-maat-gold" />
          {t.methodology.clickToEnlarge}
        </span>
      </div>
    </button>
  );

  const pillarIcons = [Cog, Minimize2, Sparkles];

  const blocks: { icon: React.ComponentType<{ size?: number; strokeWidth?: number }>; b: { label: string; tagline: string; desc: string }; badge?: boolean; images: [string, string][] }[] = [
    { icon: Workflow, b: id.blocks.businessAnalysis, images: [['/business-process.jpg', id.captions.businessProcess]] },
    { icon: Compass, b: id.blocks.charterRoadmap, images: [['/project-design-scenarios.jpg', id.captions.scenarios], ['/project-of-projects.jpg', id.captions.projects]] },
    { icon: Boxes, b: id.blocks.systemDesign, badge: true, images: [['/use-case-to-architecture.jpg', id.captions.useCase], ['/idesign-example-arch.jpg', id.captions.arch]] },
    { icon: Map, b: id.blocks.projectDesign, badge: true, images: [['/project-plan.png', id.captions.plan]] },
    { icon: Code2, b: id.blocks.detailedDesign, images: [['/documentation-landscape.jpg', id.captions.landscape], ['/sequence-diagram.jpg', id.captions.sequenceDiagram]] },
    { icon: Rocket, b: id.blocks.execution, images: [['/service-development-lifecycle.jpg', id.captions.serviceLifecycle]] },
  ];

  return (
    <div className="bg-maat-stone min-h-screen">
      <PageHeader
        tag={t.methodology.tag}
        title={t.methodology.title}
        titleAccent={t.methodology.titleAccent}
        subtitle={t.methodology.subtitle}
      />

      {/* Sounds familiar? */}
      <section className="pt-24 pb-20 relative overflow-hidden bg-[#faf7f2]/40">
        <div className="container mx-auto px-6 max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif text-maat-blue font-bold">{t.methodology.pains.title}</h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {t.methodology.pains.items.map((item: string, idx: number) => {
              // Rotation classes based on index
              const rotations = [
                'rotate-[-1.5deg] md:rotate-[-2deg]',
                'rotate-[1.5deg] md:rotate-[2deg]',
                'rotate-[-1deg] md:rotate-[-1deg]',
                'rotate-[1deg] md:rotate-[1deg]'
              ];
              const rotationClass = rotations[idx % rotations.length];

              // Background tint based on index
              const backgrounds = [
                'bg-white',
                'bg-[#FDFBF7]',
                'bg-[#FAF6EC]'
              ];
              const bgClass = backgrounds[idx % backgrounds.length];

              const categoryInfo = painCategories[idx];
              const CategoryIcon = categoryInfo ? categoryInfo.icon : Code2;
              const categoryLabel = categoryInfo ? categoryInfo.label : '';

              return (
                <div 
                  key={idx} 
                  className={`relative ${bgClass} border border-maat-sand/80 p-8 rounded-3xl shadow-[2px_8px_16px_rgba(5,10,48,0.03)] hover:shadow-xl hover:-translate-y-1.5 hover:rotate-0 hover:border-maat-gold/40 transition-all duration-300 z-10 flex flex-col justify-between min-h-[140px] transform ${rotationClass}`}
                >
                  {/* Tape effect */}
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 w-16 h-5 bg-maat-gold/15 backdrop-blur-[1px] rotate-[-2deg] shadow-sm border border-white/10 z-20"></div>
                  
                  {/* Subtle Category Icon */}
                  {categoryInfo && (
                    <div 
                      className="absolute top-4 right-4 text-maat-gold/30 hover:text-maat-gold/60 transition-colors z-20 cursor-help"
                      title={categoryLabel}
                    >
                      <CategoryIcon size={16} strokeWidth={2.5} />
                    </div>
                  )}

                  <span className="text-maat-blue/90 font-sans text-[15px] font-medium leading-relaxed pt-1 relative z-10">
                    {item}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Collaboration */}
      <section className="pb-24">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="bg-maat-blue text-maat-stone rounded-5xl p-10 md:p-16 shadow-2xl relative overflow-hidden">
            <div className="relative z-10 md:flex md:items-center md:gap-10">
              <div className="w-16 h-16 bg-maat-gold/15 rounded-2xl flex items-center justify-center text-maat-gold flex-shrink-0 mb-6 md:mb-0">
                <Handshake size={34} strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-serif text-white font-bold mb-4 leading-snug">{t.methodology.collab.title}</h2>
                <p className="text-maat-stone/80 leading-relaxed mb-8 max-w-2xl">{t.methodology.collab.text}</p>
                <Link to="/contact" className="inline-flex items-center gap-3 bg-maat-gold text-maat-blue px-8 py-4 rounded-2xl font-bold hover:bg-maat-stone transition-all duration-300 group shadow-xl">
                  <span>{t.methodology.collab.cta}</span>
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-maat-gold opacity-5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
          </div>
        </div>
      </section>

      {/* Principles (cross-cutting) */}
      <section className="py-16">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-serif text-maat-blue font-bold">{t.methodology.principlesTitle}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {t.methodology.pillars.map((pillar: { title: string; tagline: string; desc: string }, idx: number) => {
              const Icon = pillarIcons[idx] ?? Cog;
              return (
                <div key={idx} className="bg-white border border-maat-sand rounded-3xl p-7 text-center hover:border-maat-gold/40 transition-colors">
                  <div className="w-14 h-14 mx-auto bg-maat-blue rounded-2xl flex items-center justify-center text-maat-gold mb-4">
                    <Icon size={26} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-serif text-maat-blue font-bold">{pillar.title}</h3>
                  <p className="text-maat-gold text-xs italic mt-1 mb-3">{pillar.tagline}</p>
                  <p className="text-sm text-maat-blue/70 leading-relaxed">{pillar.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Building blocks */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-b from-maat-stone via-white to-maat-stone">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e6dccf_1px,transparent_1px),linear-gradient(to_bottom,#e6dccf_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-30 pointer-events-none"></div>

        <div className="container mx-auto px-6 max-w-6xl relative z-10">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-serif text-maat-blue mb-6 font-bold">{id.title}</h2>
            <p className="text-maat-blue/70 leading-relaxed max-w-3xl mx-auto text-lg">{id.intro}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {blocks.map((blk, idx) => (
              <div key={idx} className="bg-white border border-maat-sand rounded-3xl p-6 shadow-sm hover:border-maat-gold/40 hover:shadow-lg transition-all flex flex-col">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 bg-maat-blue rounded-2xl flex items-center justify-center text-maat-gold flex-shrink-0">
                    <blk.icon size={22} strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-serif text-maat-blue font-bold leading-tight">{blk.b.label}</h4>
                      {blk.badge && (
                        <span className="text-[9px] uppercase tracking-wider bg-maat-gold/15 text-maat-gold font-bold px-2 py-0.5 rounded-full">{id.idesignBadge}</span>
                      )}
                    </div>
                    <p className="text-maat-gold text-xs italic">{blk.b.tagline}</p>
                  </div>
                </div>
                <p className="text-sm text-maat-blue/70 leading-relaxed mb-5">{blk.b.desc}</p>
                <div className="mt-auto grid grid-cols-2 gap-3">
                  {blk.images.map(([src, caption], j) => (
                    <React.Fragment key={j}>{squareThumb(src, caption)}</React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Complete method vs. tailored building blocks */}
          <div className="grid sm:grid-cols-2 gap-4 max-w-4xl mx-auto">
            <div className="flex gap-4 items-start bg-white border border-maat-sand rounded-2xl p-5 shadow-sm">
              <div className="w-10 h-10 bg-maat-blue rounded-xl flex items-center justify-center text-maat-gold flex-shrink-0">
                <CheckCircle2 size={20} strokeWidth={1.5} />
              </div>
              <div>
                <h4 className="font-serif text-maat-blue font-bold text-base mb-1">{id.complete.wholeTitle}</h4>
                <p className="text-sm text-maat-blue/70 leading-relaxed">{id.complete.wholeText}</p>
              </div>
            </div>
            <div className="flex gap-4 items-start bg-white border-2 border-maat-gold/40 rounded-2xl p-5 shadow-sm">
              <div className="w-10 h-10 bg-maat-gold rounded-xl flex items-center justify-center text-maat-blue flex-shrink-0">
                <Blocks size={20} strokeWidth={1.5} />
              </div>
              <div>
                <h4 className="font-serif text-maat-blue font-bold text-base mb-1">{id.complete.modularTitle}</h4>
                <p className="text-sm text-maat-blue/70 leading-relaxed">{id.complete.modularText}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Have you Met Maät? (Combined features) */}
      <HomeFeatures />

      {/* Lightbox Modal */}
      {activeImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-maat-blue/95 backdrop-blur-md transition-opacity duration-300"
          onClick={() => setActiveImage(null)}
        >
          <button
            className="absolute top-6 right-6 text-white/70 hover:text-white cursor-pointer transition-colors p-2 focus:outline-none"
            onClick={() => setActiveImage(null)}
            aria-label="Close lightbox"
          >
            <X size={36} />
          </button>
          <div
            className="relative max-w-5xl max-h-[85vh] overflow-hidden rounded-2xl border border-white/10 bg-white p-2 shadow-2xl flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={activeImage.src}
              alt={activeImage.alt}
              className="max-w-full max-h-[75vh] object-contain rounded-xl"
            />
            <div className="py-3 px-4 text-center">
              <p className="text-sm font-serif text-maat-blue font-semibold">{activeImage.alt}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Methodology;
